import {
    EquipmentSurveyInput,
    AirHandlingEquipmentType,
    AirHandlingEquipmentHeatingType,
    AirHandlingEquipmentCoolingType,
    CentralPlantCoolerType,
    CentralPlantCoolerTerminalUnits,
    CentralPlantHeatPumpType,
    CentralPlantHeaterType,
    CentralPlantHeaterTerminalUnits,
    DomesticHotWaterHeaterType,
    HeatPumpType,
    RooftopUnitHeatingType,
    RooftopUnitCoolingType,
    TerminalCoolerUnits,
    TerminalHeaterUnits,
    ClothesDryersType,
} from "gql/graphql";

export const mapCSVToEquipmentSurvey = (csv: Record<string, string>): EquipmentSurveyInput => {
    // These functions convert CSV data (always string) to corresponding data type
    const parseBoolean = (value: string): boolean => value?.toLowerCase() === "true" || value === "1";

    const parseNumber = (value: string): number | undefined => {
        const num = parseFloat(value);
        return Number.isNaN(num) ? undefined : num;
    };

    const parseInt = (value: string): number | undefined => {
        const num = Number.parseInt(value, 10);
        return Number.isNaN(num) ? undefined : num;
    };

    return {
        airHandlingEquipment: {
            airHandlingEquipmentExists: parseBoolean(csv.airHandlingEquipmentExists),
            airHandlingEquipmentType: (csv.airHandlingEquipmentType as AirHandlingEquipmentType) || undefined,
            airHandlingEquipmentHeatingType:
                (csv.airHandlingEquipmentHeatingType as AirHandlingEquipmentHeatingType) || undefined,
            airHandlingEquipmentCoolingType:
                (csv.airHandlingEquipmentCoolingType as AirHandlingEquipmentCoolingType) || undefined,
            airHandlingEquipmentSupplyAirRate: parseNumber(csv.airHandlingEquipmentSupplyAirRate),
            airHandlingEquipmentAverageInstallationYear: parseInt(csv.airHandlingEquipmentAverageInstallationYear),
        },
        centralPlantCooler: {
            centralPlantCoolerExists: parseBoolean(csv.centralPlantCoolerExists),
            centralPlantCoolerType: (csv.centralPlantCoolerType as CentralPlantCoolerType) || undefined,
            centralPlantCoolerTerminalUnits:
                (csv.centralPlantCoolerTerminalUnits as CentralPlantCoolerTerminalUnits) || undefined,
            centralPlantCoolerSize: parseNumber(csv.centralPlantCoolerSize),
            centralPlantCoolerAverageInstallationYear: parseInt(csv.centralPlantCoolerAverageInstallationYear),
        },
        centralPlantHeatPump: {
            centralPlantHeatPumpExists: parseBoolean(csv.centralPlantHeatPumpExists),
            centralPlantHeatPumpType: (csv.centralPlantHeatPumpType as CentralPlantHeatPumpType) || undefined,
            centralPlantHeatPumpSize: parseNumber(csv.centralPlantHeatPumpSize),
            centralPlantHeatPumpAverageInstallationYear: parseInt(csv.centralPlantHeatPumpAverageInstallationYear),
        },
        centralPlantHeater: {
            centralPlantHeaterExists: parseBoolean(csv.centralPlantHeaterExists),
            centralPlantHeaterType: (csv.centralPlantHeaterType as CentralPlantHeaterType) || undefined,
            centralPlantHeaterTerminalUnits:
                (csv.centralPlantHeaterTerminalUnits as CentralPlantHeaterTerminalUnits) || undefined,
            centralPlantHeaterSize: parseNumber(csv.centralPlantHeaterSize),
            centralPlantHeaterAverageInstallationYear: parseInt(csv.centralPlantHeaterAverageInstallationYear),
        },
        domesticHotWaterHeater: {
            domesticHotWaterHeaterExists: parseBoolean(csv.domesticHotWaterHeaterExists),
            domesticHotWaterHeaterCentralDistribution: parseBoolean(csv.domesticHotWaterHeaterCentralDistribution),
            domesticHotWaterHeaterType: (csv.domesticHotWaterHeaterType as DomesticHotWaterHeaterType) || undefined,
            domesticHotWaterHeaterSize: parseNumber(csv.domesticHotWaterHeaterSize),
            domesticHotWaterHeaterAverageInstallationYear: parseInt(csv.domesticHotWaterHeaterAverageInstallationYear),
        },
        heatPump: {
            heatPumpExists: parseBoolean(csv.heatPumpExists),
            heatPumpType: (csv.heatPumpType as HeatPumpType) || undefined,
            heatPumpSize: parseNumber(csv.heatPumpSize),
            heatPumpInstallationYear: parseInt(csv.heatPumpInstallationYear),
            heatPumpCoolingCoefficientOfPerformance: parseNumber(csv.heatPumpCoolingCoefficientOfPerformance),
            heatPumpCoolingLoadRatio: parseNumber(csv.heatPumpCoolingLoadRatio),
            heatPumpHeatingCoefficientOfPerformance: parseNumber(csv.heatPumpHeatingCoefficientOfPerformance),
            heatPumpHeatingLoadRatio: parseNumber(csv.heatPumpHeatingLoadRatio),
        },
        rooftopUnit: {
            rooftopUnitExists: parseBoolean(csv.rooftopUnitExists),
            rooftopUnitHeatingType: (csv.rooftopUnitHeatingType as RooftopUnitHeatingType) || undefined,
            rooftopUnitCoolingType: (csv.rooftopUnitCoolingType as RooftopUnitCoolingType) || undefined,
            rooftopUnitSupplyAirRate: parseNumber(csv.rooftopUnitSupplyAirRate),
            rooftopUnitAverageInstallationYear: parseInt(csv.rooftopUnitAverageInstallationYear),
        },
        terminalCooler: {
            terminalCoolerExists: parseBoolean(csv.terminalCoolerExists),
            terminalCoolerUnits: (csv.terminalCoolerUnits as TerminalCoolerUnits) || undefined,
            terminalCoolerSize: parseNumber(csv.terminalCoolerSize),
            terminalCoolerAverageInstallationYear: parseInt(csv.terminalCoolerAverageInstallationYear),
        },
        terminalHeater: {
            terminalHeaterExists: parseBoolean(csv.terminalHeaterExists),
            terminalHeaterUnits: (csv.terminalHeaterUnits as TerminalHeaterUnits) || undefined,
            terminalHeaterSize: parseNumber(csv.terminalHeaterSize),
            terminalHeaterCoolerSize: parseNumber(csv.terminalHeaterCoolerSize),
            terminalHeaterAverageInstallationYear: parseInt(csv.terminalHeaterAverageInstallationYear),
        },
        otherEquipment: {
            clothesDryersExists: parseBoolean(csv.clothesDryersExists),
            clothesDryersType: (csv.clothesDryersType as ClothesDryersType) || undefined,
            clothesDryersEnergyDensity: parseNumber(csv.clothesDryersEnergyDensity),
            clothesDryersAverageInstallationYear: parseInt(csv.clothesDryersAverageInstallationYear),
            clothesWashersExists: parseBoolean(csv.clothesWashersExists),
            clothesWashersEnergyDensity: parseNumber(csv.clothesWashersEnergyDensity),
            clothesWashersAverageInstallationYear: parseInt(csv.clothesWashersAverageInstallationYear),
            elevatorsExists: parseBoolean(csv.elevatorsExists),
            elevatorsQuantity: parseInt(csv.elevatorsQuantity),
            elevatorsAverageInstallationYear: parseInt(csv.elevatorsAverageInstallationYear),
            escalatorExists: parseBoolean(csv.escalatorExists),
            escalatorQuantity: parseInt(csv.escalatorQuantity),
            escalatorInstallationYear: parseInt(csv.escalatorInstallationYear),
            escalatorFloorAreaServed: parseInt(csv.escalatorFloorAreaServed),
            escalatorVariableFrequencyDrive: parseBoolean(csv.escalatorVariableFrequencyDrive),
            rooftopPhotovoltaicsExists: parseBoolean(csv.rooftopPhotovoltaicsExists),
            rooftopPhotovoltaicsSize: parseNumber(csv.rooftopPhotovoltaicsSize),
            rooftopPhotovoltaicsAverageInstallationYear: parseInt(csv.rooftopPhotovoltaicsAverageInstallationYear),
        },
        genericHVACEquipment: csv.name
            ? [
                  {
                      name: csv.name ?? "",
                      size: parseNumber(csv.size) ?? 0,
                      sizeUnits: csv.sizeUnits ?? "",
                      fuel: csv.fuel ?? "",
                      endUse: csv.endUse ?? "",
                      installationYear: parseInt(csv.installationYear) ?? 0,
                      coefficientOfPerformance: parseNumber(csv.coefficientOfPerformance) ?? 0,
                      loadRatio: parseNumber(csv.loadRatio) ?? 0,
                      lifeSpan: parseInt(csv.lifeSpan) ?? 0,
                  },
              ]
            : [],
    };
};
